import type { TFunction } from '@mint-lib/i18n';

import ContentWithCheckbox from '../atoms/InsertionOrderModal/ContentWithCheckbox.jsx';
import InsertionOrderIdList from '../atoms/InsertionOrderModal/InsertionOrderIdList.jsx';
import {
  ModalType,
  type GetModalContent,
  type TooltipConditions,
} from '../types/insertionOrder.js';

export const modalContentMap = {
  [ModalType.LIST]: ({ data, selectInsertionOrder, t }: GetModalContent) =>
    ({
      title: t('Insertion Order creation'),
      content: (
        <InsertionOrderIdList
          data={data}
          selectInsertionOrder={selectInsertionOrder}
          text={t(
            "Choose from the existing Insertion Orders associated with this platform which one you'd like to resend",
          )}
        />
      ),
    }) as const,
  [ModalType.LIST_WITH_NEW]: ({
    data,
    selectInsertionOrder,
    t,
  }: GetModalContent) =>
    ({
      title: t('Insertion Order creation'),
      content: (
        <InsertionOrderIdList
          data={data}
          selectInsertionOrder={selectInsertionOrder}
          text={t(
            'When creating an Insertion Order at platform level, new media row(s) can be merged into the existing Insertion Order(s) or assigned to a new one.',
          )}
          alertData={t(
            'Adding new media row(s) to existing Insertion Orders simplifies updates and resubmissions.',
          )}
        />
      ),
    }) as const,
  [ModalType.EXISTING]: ({ t, toggleDoNotShowAgain }: GetModalContent) =>
    ({
      title: t('Insertion Order already existing'),
      content: (
        <ContentWithCheckbox
          content={t(
            'The selected media row is already in an existing Insertion Order. Continuing will include all linked media rows.',
          )}
          toggleDoNotShowAgain={toggleDoNotShowAgain}
        />
      ),
    }) as const,
  [ModalType.CREATION]: ({ t, toggleDoNotShowAgain }: GetModalContent) =>
    ({
      title: t('Insertion Order creation'),
      content: (
        <ContentWithCheckbox
          content={t(
            'When activating the Insertion Order at the media row level, each row receives a unique ID upon creation.',
          )}
          toggleDoNotShowAgain={toggleDoNotShowAgain}
          alertData={t(
            'Creating the IO at the platform level ensures all media rows share the same ID, simplifying future updates.',
          )}
        />
      ),
    }) as const,
  [ModalType.DEFAULT]: () =>
    ({
      title: '',
      content: '',
    }) as const,
};

export enum IOErrorCodes {
  ADVERTISER_ERR = 'advertiser_err',
  MEDIA_PLAN_NOT_FOUND = 'not_found',
  NO_MEDIA_ROWS = 'no_media_rows',
  BUYING_PLAN_NOT_FOUND = 'buying_plan_not_found',
  IO_NOT_FOUND = 'io_not_found',
  MISSED_MEDIA_ROWS = 'missed_media_rows',
}

export const getTooltipTextConditions = (
  {
    isLoading,
    isPlatformLevel,
    hasSubRows,
    isIoCanBeSent,
    isValidIoGroup,
    isBuyingPlanApproved,
    isUserPartOfIoGroup,
    isReadOnlyUser,
  }: TooltipConditions,
  t: TFunction,
) => [
  {
    condition: isLoading,
    message: '',
  },
  {
    condition: isReadOnlyUser,
    message: t('Insertion Order sending is restricted due to user permissions'),
  },
  {
    condition: isPlatformLevel && !hasSubRows,
    message: t(
      "Insertion order can't be created since the platform does not contain media rows",
    ),
  },
  {
    condition: isPlatformLevel && !isIoCanBeSent,
    message: t(
      'The media rows for this platform cannot be sent as an Insertion Order. Mandatory information missing',
    ),
  },
  {
    condition: !isIoCanBeSent,
    message: t(
      'Add net media budget, dates, price type and value to create Insertion Order',
    ),
  },
  {
    condition: !isUserPartOfIoGroup,
    message: t(
      'You must be a part of a group to send an Insertion Order. Please go to the Company Profile.',
    ),
  },
  {
    condition: !isValidIoGroup,
    message: t(
      'This IO has been sent by a user from another IO group, and you can not send it again.',
    ),
  },
  {
    condition: !isBuyingPlanApproved,
    message: t(
      'Insertion orders can only be sent after the Buying Plan is approved',
    ),
  },
];
